import { useEffect, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import './chatBoxWindow.css';
import AssistantRoundedIcon from '@mui/icons-material/AssistantRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import { copyToClipboard } from 'utils/stringUtils';

/**
 * Renders a chat window displaying a list of messages. Messages can be sent by either the user or the AI assistant.
 *
 * @param {Object[]} messages - Array of message objects to be displayed.
 * @param {string} messages[].id - Unique identifier for the message.
 * @param {string} messages[].sender - The sender of the message, either 'user' or 'ai'.
 * @param {string} messages[].text - The content of the message.
 *
 * @returns {React.Element} The rendered chat window component.
 */
export default function ChatBoxWindow({ messages }) {
  const { formatMessage: msg } = useIntl();
  const messagesEndRef = useRef(null);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);

  useEffect(() => {
    const container = messagesEndRef.current;
    container?.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);

  const handleCopyBtnClick = async (text, messageIndex) => {
    await copyToClipboard(text);
    setCopiedMessageIndex(messageIndex);

    setTimeout(() => {
      setCopiedMessageIndex(null);
    }, 2000);
  };

  return (
    <MDBox
      ref={messagesEndRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '1000px',
        scrollbarWidth: 'thin'
      }}
    >
      {messages.map(({ id, sender, text }, index) => (
        <Box
          key={id ?? index}
          sx={{
            marginBottom: 2,
            padding: 1,
            borderRadius: '10px',
            backgroundColor: sender === SenderEnum.USER ? '#e5e5ea' : 'rgba(0, 0, 0, 0)',
            maxWidth: '85%',
            alignSelf: sender === SenderEnum.USER ? 'flex-end' : 'flex-start',
            textAlign: sender === SenderEnum.USER ? 'right' : 'left'
          }}
        >
          {sender === SenderEnum.AI ? (
            <MDBox display="flex">
              <AssistantRoundedIcon fontSize="large" sx={{ marginRight: '1rem' }} />
              <MDBox
                flex={1}
                styleClass="line-break"
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <ReactMarkdown>{text}</ReactMarkdown>
                {index > 0 && (
                  <MDBox id="buttonBar" display="flex" sx={{ marginLeft: '-9px' }}>
                    <Tooltip title={msg({ id: 'button.copy' })}>
                      <IconButton
                        onClick={() => handleCopyBtnClick(text, index)}
                        color="text"
                        size="small"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            borderRadius: '15%'
                          }
                        }}
                      >
                        {copiedMessageIndex === index ? (
                          <DoneRoundedIcon />
                        ) : (
                          <ContentCopyRoundedIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          ) : (
            <MDTypography variant="body2">{text}</MDTypography>
          )}
        </Box>
      ))}
    </MDBox>
  );
}

export const SenderEnum = {
  USER: 'user',
  AI: 'ai'
};

ChatBoxWindow.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      sender: PropTypes.oneOf([SenderEnum.USER, SenderEnum.AI]).isRequired,
      text: PropTypes.string.isRequired
    })
  ).isRequired
};
